import React, { useState, useEffect, useCallback } from 'react';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import UserJourneyExplorer from './UserJourneyExplorer';
import {
  ONBOARDING_EVENTS, SCAN_EVENTS, INTERACTION_EVENTS, CHART_COLORS,
  fetchEventsByType, formatDuration, parsePayload,
  computeOverviewStats, computeStepFunnel, computeStepDropoff,
  computeStepDurations, computeTrends, computeModeBreakdown, computeSkippedSteps,
  computeScanPerformance, computeVideoEngagement, computeStepInteractions,
} from './onboardingAnalyticsHelpers';

// ── Component ─────────────────────────────────────────────────────────
const OnboardingAnalyticsTab = ({ dateRange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subTab, setSubTab] = useState('overview');

  // Processed data
  const [overview, setOverview] = useState(null);
  const [funnelData, setFunnelData] = useState([]);
  const [stepDurations, setStepDurations] = useState([]);
  const [trends, setTrends] = useState([]);
  const [modeBreakdown, setModeBreakdown] = useState([]);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [serverFunnel, setServerFunnel] = useState(null);
  const [scanPerformance, setScanPerformance] = useState(null);
  const [videoEngagement, setVideoEngagement] = useState(null);
  const [stepInteractions, setStepInteractions] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fromDate = new Date(dateRange.from).toISOString();
      const toDate = new Date(dateRange.to + 'T23:59:59').toISOString();

      // Fetch all onboarding event types in parallel
      const [started, , stepSkipped, stepViewed, stepDuration, completed, abandoned] = await Promise.all(
        ONBOARDING_EVENTS.map(et => fetchEventsByType(et, fromDate, toDate))
      );

      // Server-side funnel (best-effort — don't block dashboard on failure)
      let funnel = null;
      try {
        funnel = await AnalyticsApi.analyzeFunnel({
          steps: [
            { eventType: 'ONBOARDING_STARTED' },
            { eventType: 'ONBOARDING_STEP_COMPLETED' },
            { eventType: 'ONBOARDING_COMPLETED' },
          ],
          sessionBased: false,
          from: fromDate,
          to: toDate,
        });
      } catch { /* ignore — client funnel is the fallback */ }
      setServerFunnel(funnel);

      // Process
      setOverview(computeOverviewStats(started, completed, abandoned));

      const funnel_ = computeStepFunnel(stepViewed);
      setFunnelData(computeStepDropoff(funnel_));

      setStepDurations(computeStepDurations(stepDuration));
      setTrends(computeTrends(started, completed));
      setModeBreakdown(computeModeBreakdown(started));
      setSkippedSteps(computeSkippedSteps(stepSkipped));

      // Fetch scan and interaction events (best-effort — don't block on failure)
      try {
        const [scanEvents, videoEvents, interactions] = await Promise.all([
          ...SCAN_EVENTS.map(et => fetchEventsByType(et, fromDate, toDate)),
          ...INTERACTION_EVENTS.map(et => fetchEventsByType(et, fromDate, toDate)),
        ]);

        // Split consolidated events by action field
        const byAction = (events, action) => events.filter(e => {
          const p = parsePayload(e);
          return p.action === action;
        });
        const scanCompleted = byAction(scanEvents, 'scan_completed');
        const scanErrors = byAction(scanEvents, 'scan_error');
        const scanTimeouts = byAction(scanEvents, 'scan_timeout');
        const videoSelected = byAction(videoEvents, 'video_selected');
        const videoDuration = byAction(videoEvents, 'video_duration');
        const scanIdle = byAction(videoEvents, 'idle');
        const scanSkip = byAction(interactions, 'scan_skip');

        setScanPerformance(computeScanPerformance(scanCompleted, scanErrors, scanTimeouts));
        setVideoEngagement(computeVideoEngagement(videoSelected, videoDuration, scanIdle, scanSkip));
        setStepInteractions(computeStepInteractions(interactions));
      } catch { /* scan/interaction analytics are supplementary — don't fail the whole tab */ }

    } catch (err) {
      console.error('Failed to fetch onboarding analytics:', err);
      setError(err.message || 'Failed to load onboarding analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Render ──────────────────────────────────────────────────────────
  const hasData = overview && overview.totalStarted > 0;

  const subTabBar = (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
      {[{ key: 'overview', label: 'Overview' }, { key: 'journeys', label: 'User Journeys' }].map(t => (
        <button key={t.key} onClick={() => setSubTab(t.key)} style={{
          padding: '8px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          background: subTab === t.key ? 'var(--action-primary-bg, #4a90e2)' : 'var(--card-bg)',
          color: subTab === t.key ? '#fff' : 'var(--text-primary)',
          border: subTab === t.key ? 'none' : '1px solid var(--border-light)',
        }}>{t.label}</button>
      ))}
    </div>
  );

  // Always show tab bar + journey tab regardless of overview loading state
  if (subTab === 'journeys') {
    return (
      <>
        {subTabBar}
        <UserJourneyExplorer dateRange={dateRange} />
      </>
    );
  }

  // Overview sub-tab: show loading/error/empty states
  if (error) return <>{subTabBar}<div className="error-message">{error}</div></>;
  if (loading) return <>{subTabBar}<div className="loading-spinner">Loading onboarding analytics…</div></>;

  if (!hasData) {
    return (
      <>
        {subTabBar}
        <div className="section-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Onboarding Analytics</h2>
          <p style={{ color: 'var(--text-muted)' }}>
            No onboarding events found for this date range. Users must go through the onboarding wizard to generate data.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {subTabBar}

      {/* Overview stat cards */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-label">Onboardings Started</div>
          <div className="stat-value">{overview.totalStarted.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completion Rate</div>
          <div className="stat-value">{overview.completionRate.toFixed(1)}%</div>
          <div className="stat-subtitle">{overview.totalCompleted.toLocaleString()} completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Abandonment Rate</div>
          <div className="stat-value">{overview.abandonmentRate.toFixed(1)}%</div>
          <div className="stat-subtitle">{overview.totalAbandoned.toLocaleString()} abandoned</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Duration</div>
          <div className="stat-value">{formatDuration(overview.avgDurationMs)}</div>
        </div>
        {scanPerformance && scanPerformance.totalScans > 0 && (
          <>
            <div className="stat-card">
              <div className="stat-label">Avg Scan Duration</div>
              <div className="stat-value">{formatDuration(scanPerformance.avgScanDurationMs)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Scan Error Rate</div>
              <div className="stat-value">{scanPerformance.scanErrorRate.toFixed(1)}%</div>
              <div className="stat-subtitle">{scanPerformance.scanErrorCount} errors / {scanPerformance.totalScans} scans</div>
            </div>
          </>
        )}
      </div>

      {/* Server-computed funnel (if available) */}
      {serverFunnel && serverFunnel.steps && serverFunnel.steps.length > 0 && (
        <div className="section-card">
          <h2>Onboarding Conversion Funnel</h2>
          <div className="funnel-visualization">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={serverFunnel.steps} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="stepLabel" type="category" width={160} />
                <Tooltip />
                <Legend />
                <Bar dataKey="usersEntered" fill="#4a90e2" name="Users Entered" />
                <Bar dataKey="usersCompleted" fill="#50c878" name="Users Completed" />
              </BarChart>
            </ResponsiveContainer>
            <div className="funnel-metrics">
              <div className="metric-item">
                <span className="metric-label">Overall Conversion:</span>
                <span className="metric-value">{serverFunnel.overallConversionRate?.toFixed(1)}%</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Total Entered:</span>
                <span className="metric-value">{serverFunnel.totalUsersEntered?.toLocaleString()}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Total Completed:</span>
                <span className="metric-value">{serverFunnel.totalUsersCompleted?.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step-level drop-off */}
      {funnelData.length > 0 && (
        <div className="section-card">
          <h2>Step-Level Drop-off</h2>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={funnelData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" angle={-30} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'Drop-off %') return `${value}%`;
                  return value.toLocaleString();
                }}
              />
              <Legend />
              <Bar dataKey="users" fill="#4a90e2" name="Users Reached" />
              <Bar dataKey="dropoff" fill="#e94b3c" name="Drop-off %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Step Duration */}
      {stepDurations.length > 0 && (
        <div className="section-card">
          <h2>Average Time Per Step (seconds)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stepDurations}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" angle={-30} textAnchor="end" height={80} />
              <YAxis unit="s" />
              <Tooltip formatter={(v) => `${v}s`} />
              <Bar dataKey="avgSeconds" fill="#f5a623" name="Avg Seconds" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Trends over time */}
      {trends.length > 0 && (
        <div className="section-card">
          <h2>Onboarding Trends (Daily)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="started" fill="#4a90e2" name="Started" />
              <Bar dataKey="completed" fill="#50c878" name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Mode breakdown + Skipped steps side by side */}
      <div className="chart-grid">

        {/* ── Scan Performance ── */}
        {scanPerformance && scanPerformance.totalScans > 0 && (
          <div className="section-card">
            <h2>Scan Duration Distribution</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={scanPerformance.histogram}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#9b59b6" name="Scans" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
              <span>Median: {formatDuration(scanPerformance.medianScanDurationMs)}</span>
              <span>Avg Wait: {formatDuration(scanPerformance.avgWaitDurationMs)}</span>
            </div>
          </div>
        )}

        {/* ── Video Engagement ── */}
        {videoEngagement && videoEngagement.videoSelections.length > 0 && (
          <div className="section-card">
            <h2>Tutorial Video Engagement</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={videoEngagement.videoSelections} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="video" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="selections" fill="#1abc9c" name="Times Selected" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Video Watch Duration ── */}
        {videoEngagement && videoEngagement.avgWatchDurations.length > 0 && (
          <div className="section-card">
            <h2>Avg Video Watch Duration</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={videoEngagement.avgWatchDurations} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit="s" />
                <YAxis dataKey="video" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v}s`} />
                <Bar dataKey="avgSeconds" fill="#f5a623" name="Avg Seconds" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Scan Wait Behavior ── */}
        {videoEngagement && videoEngagement.waitBehavior.length > 0 && (
          <div className="section-card">
            <h2>Scan Wait Behavior</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={videoEngagement.waitBehavior}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {videoEngagement.waitBehavior.map((_, idx) => (
                    <Cell key={`wait-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Scan Duration Trend ── */}
        {scanPerformance && scanPerformance.dailyTrend.length > 1 && (
          <div className="section-card">
            <h2>Avg Scan Duration Over Time</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={scanPerformance.dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis unit="s" />
                <Tooltip formatter={(v) => `${v}s`} />
                <Bar dataKey="avgSeconds" fill="#9b59b6" name="Avg Seconds" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Mode Breakdown */}
        {modeBreakdown.length > 0 && (
          <div className="section-card">
            <h2>Onboarding Mode</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={modeBreakdown}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {modeBreakdown.map((_, idx) => (
                    <Cell key={`mode-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Skipped Steps */}
        {skippedSteps.length > 0 && (
          <div className="section-card">
            <h2>Most Skipped Steps</h2>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Times Skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedSteps.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{row.step}</td>
                      <td>{row.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Per-Step Interaction Drilldown ── */}
      {stepInteractions.length > 0 && (
        <div className="section-card">
          <h2>Per-Step Interaction Drilldown</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Click a step to expand and see what users are doing within each onboarding step.
          </p>
          {stepInteractions.map((si) => {
            const isExpanded = expandedSteps.has(si.step);
            return (
              <div key={si.step} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', marginBottom: '4px' }}>
                <button
                  onClick={() => setExpandedSteps(prev => {
                    const next = new Set(prev);
                    if (next.has(si.step)) next.delete(si.step);
                    else next.add(si.step);
                    return next;
                  })}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', padding: '10px 4px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                    color: 'var(--text-primary, inherit)',
                  }}
                >
                  <span>{isExpanded ? '\u25BE' : '\u25B8'} {si.step}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 400 }}>
                    {si.totalInteractions.toLocaleString()} interactions
                  </span>
                </button>
                {isExpanded && (
                  <div style={{ padding: '0 16px 12px' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Action</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {si.actions.map((a, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border-color, #f0f0f0)' }}>
                            <td style={{ padding: '6px 8px' }}>{a.action}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>{a.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default OnboardingAnalyticsTab;
